import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import type { FormValues } from '@/lib/types';
import type { SopAnalysisResult } from '@/components/sop-intake/sop-analyzer/types';
import { cn } from '@/lib/utils';
import {
  getBiasReason,
  getRuleDocBiomechNote,
  getRuleDocExpectedRlp,
} from '@/components/sop-intake/sop-analyzer/lateralization-explainer';
import { getSimpleIntakeLateralizationState } from '@/components/sop-intake/sop-analyzer/engine/table-spectrum';
import {
  filterSpectrumDefinitions,
  isGroundingBatteryVisible,
} from '@/components/sop-intake/sop-analyzer/visible-battery';
import { useSettings } from '@/components/settings/lib/settings-context';
import type { RuleDocVersion } from '@/lib/posture-analysis/rule-params';

interface LateralizationDetailDrawerProps {
  analysis: SopAnalysisResult;
  formValues: FormValues;
  viewMode?: 'simple' | 'detailed';
  visibleTestKeys?: ReadonlySet<string>;
}

const SPECTRUM_COL_HEADERS = [
  'Func. Occ.',
  'Biased Lat.',
  'Mixed Lat.',
  'Mixed Lat.',
  'Biased Lat.',
  'Func. Occ.',
];

const activeCellStyle = (colIndex: number): string =>
  colIndex <= 2
    ? 'bg-accent/25 text-accent font-semibold ring-1 ring-inset ring-accent/40'
    : 'bg-status-deviation-surface text-status-deviation font-semibold ring-1 ring-inset ring-status-deviation';

const parseGroundingMode = (formValues: FormValues): 'dynamometer' | 'perceived' =>
  String(formValues.GroundingMode || '').toLowerCase() === 'perceived' ? 'perceived' : 'dynamometer';

const formatGrounding = (formValues: FormValues, side: 'L' | 'R', mode: 'dynamometer' | 'perceived'): string => {
  const preferredKey = mode === 'perceived' ? `${side}_GroundingPerformance_perceived` : `${side}_GroundingPerformance_actual`;
  const secondaryKey = mode === 'perceived' ? `${side}_GroundingPerformance_actual` : `${side}_GroundingPerformance_perceived`;
  const candidates = [
    formValues[preferredKey as keyof FormValues],
    formValues[secondaryKey as keyof FormValues],
    formValues[`${side}_GroundingPerformance` as keyof FormValues],
  ];
  const value = candidates.find((item) => item !== null && item !== undefined && String(item).trim() !== '');
  if (!value) return 'N/A';
  return `${String(value)} (${mode === 'dynamometer' ? 'D' : 'P'})`;
};

const fmtVal = (v: number | null): string => (v === null ? '—' : `${v}°`);

export default function LateralizationDetailDrawer({
  analysis,
  formValues,
  viewMode = 'simple',
  visibleTestKeys,
}: LateralizationDetailDrawerProps) {
  const { settings } = useSettings();
  const ruleDocVersion: RuleDocVersion = settings.useLegacySopAssessment ? 'legacy' : 'current';
  const tests = analysis.lateralization.tests;
  const spectrumDefinitions = visibleTestKeys
    ? filterSpectrumDefinitions(analysis, visibleTestKeys)
    : analysis.spectrum.definitions;
  const spectrumRowsByKey = analysis.spectrum.rowsByKey;
  const showGroundingPanel = visibleTestKeys ? isGroundingBatteryVisible(visibleTestKeys) : true;
  const groundingMode = parseGroundingMode(formValues);
  const groundingL = formatGrounding(formValues, 'L', groundingMode);
  const groundingR = formatGrounding(formValues, 'R', groundingMode);
  const footPresentation = formValues.FootPresentation ? String(formValues.FootPresentation) : 'N/A';

  const thBase = 'px-2 py-2 text-[10px] font-semibold uppercase tracking-wide text-muted whitespace-nowrap';
  const tdBase = 'px-2 py-2 text-xs border-t border-accent/20';
  const tooltipClass = 'max-w-sm text-xs leading-snug';
  const isSimpleView = viewMode === 'simple';

  return (
    <div className="flex h-full w-full flex-col gap-5 px-0 pb-10 pt-2 sm:px-2">
      <div className="flex flex-col gap-2 mb-2">
        <h3 className="text-xl font-semibold text-fg">Lateralization Detail Table</h3>
        <p className="text-sm text-muted">
          Exact values and reasoning behind each directional indicator.
        </p>
      </div>

          {showGroundingPanel && (
          <div className="flex flex-wrap items-stretch gap-3">
            <div className="flex min-w-[140px] flex-1 flex-col items-center justify-center rounded-lg border border-accent/40 bg-surface p-3">
              <span className="text-[10px] uppercase tracking-wide text-muted">Grounding (L)</span>
              <span className="mt-1 text-xl font-mono text-fg">{groundingL}</span>
            </div>
            <div className="flex min-w-[140px] flex-1 flex-col items-center justify-center rounded-lg border border-accent/40 bg-surface p-3">
              <span className="text-[10px] uppercase tracking-wide text-muted">Foot Presentation / Grounding Mode</span>
              <span className="mt-1 text-xs font-semibold uppercase text-muted">
                {groundingMode === 'dynamometer' ? 'Dynamometer active' : 'Perceived active'}
              </span>
              <span className="mt-1 text-base font-semibold text-accent">{footPresentation}</span>
            </div>
            <div className="flex min-w-[140px] flex-1 flex-col items-center justify-center rounded-lg border border-accent/40 bg-surface p-3">
              <span className="text-[10px] uppercase tracking-wide text-muted">Grounding (R)</span>
              <span className="mt-1 text-xl font-mono text-fg">{groundingR}</span>
            </div>
          </div>
          )}

          {(analysis.orientation.flags.length > 0 || analysis.orientation.hypermobilityFlags.length > 0) && (
            <div className="flex flex-wrap gap-2">
              {analysis.orientation.flags.map((flag) => (
                <span key={flag} className="rounded-md border border-accent/40 bg-surface px-3 py-1.5 text-xs text-muted">
                  {flag}
                </span>
              ))}
              {analysis.orientation.hypermobilityFlags.map((flag) => (
                <span key={flag} className="rounded-md border border-status-warn/60 bg-status-warn-surface px-3 py-1.5 text-xs text-status-warn">
                  {flag}
                </span>
              ))}
            </div>
          )}

          <div className={cn('max-w-full rounded-xl border border-accent/40 bg-surface', isSimpleView ? 'overflow-x-hidden' : 'overflow-x-auto')}>
            <Table className={cn('w-full border-collapse text-muted', isSimpleView ? 'table-fixed' : 'min-w-[1000px]')}>
              <TableHeader>
                {isSimpleView ? (
                  <TableRow>
                    <TableHead className={cn(thBase, 'sticky left-0 z-10 whitespace-normal bg-surface text-left')}>Test</TableHead>
                    <TableHead className={cn(thBase, 'w-12 whitespace-normal text-center sm:w-[70px]')}>Goal</TableHead>
                    <TableHead className={cn(thBase, 'w-12 whitespace-normal text-center sm:w-[70px]')}>[L]</TableHead>
                    <TableHead className={cn(thBase, 'w-12 whitespace-normal text-center sm:w-[70px]')}>[R]</TableHead>
                    <TableHead className={cn(thBase, 'hidden text-center text-accent sm:table-cell sm:w-[130px]')}>L Lateralized</TableHead>
                    <TableHead className={cn(thBase, 'hidden text-center text-status-deviation sm:table-cell sm:w-[130px]')}>R Lateralized</TableHead>
                  </TableRow>
                ) : (
                  <>
                    <TableRow>
                      <TableHead rowSpan={2} className={cn(thBase, 'text-left w-[160px] sticky left-0 bg-surface z-10')}>Test</TableHead>
                      <TableHead rowSpan={2} className={cn(thBase, 'w-[52px] text-center')}>Goal</TableHead>
                      <TableHead rowSpan={2} className={cn(thBase, 'w-[52px] text-center')}>[L]</TableHead>
                      <TableHead rowSpan={2} className={cn(thBase, 'w-[52px] text-center')}>[R]</TableHead>
                      <TableHead colSpan={3} className={cn(thBase, 'text-center border-b border-accent/20 text-accent')}>
                        Left-Lateralized Limitations
                      </TableHead>
                      <TableHead colSpan={3} className={cn(thBase, 'text-center border-b border-accent/20 text-status-deviation')}>
                        Right-Lateralized Limitations
                      </TableHead>
                    </TableRow>
                    <TableRow>
                      {SPECTRUM_COL_HEADERS.map((label, i) => (
                        <TableHead key={`${label}-${i}`} className={cn(thBase, 'text-center w-[96px]', i <= 2 ? 'text-accent' : 'text-status-deviation')}>
                          {label}
                        </TableHead>
                      ))}
                    </TableRow>
                  </>
                )}
              </TableHeader>
              <TableBody>
                {spectrumDefinitions.map((def) => {
                  const test = tests.find((t) => t.testKey === def.key);
                  const row = spectrumRowsByKey[def.key];
                  const rowState = row?.rowState ?? 'No Data';
                  const activeColumns = row?.activeColumns ?? [];
                  const leftValueText = row?.leftDisplay ?? fmtVal(test?.leftVal ?? null);
                  const rightValueText = row?.rightDisplay ?? fmtVal(test?.rightVal ?? null);
                  const reason = test ? getBiasReason(test) : null;
                  const isInvalidated = test?.isInvalidated ?? false;
                  const isHypermobile = test?.isHypermobile ?? false;

                  const latResult = getSimpleIntakeLateralizationState(
                    def.key,
                    test?.leftVal ?? null,
                    test?.rightVal ?? null,
                    ruleDocVersion
                  );
                  const simpleLeftActive = latResult === 'left';
                  const simpleRightActive = latResult === 'right';

                  return (
                    <TableRow
                      key={def.key}
                      className={cn(
                        'group',
                        isInvalidated && 'opacity-50',
                        isHypermobile && 'bg-status-warn-surface text-status-warn'
                      )}
                    >
                      <TableCell className={cn(tdBase, 'sticky left-0 bg-surface z-10')}>
                        <TooltipProvider delayDuration={0}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="cursor-help">
                                <div className="font-medium text-fg">{def.label}</div>
                                {!isSimpleView && (
                                  <div className="mt-1 flex flex-wrap gap-1">
                                    <Badge
                                      variant="outline"
                                      className={cn(
                                        rowState === 'Biased Lateralization' && 'border-status-clear/60 bg-status-clear-surface text-status-clear',
                                        rowState === 'Mixed Lateralization' && 'border-accent/60 bg-accent/10 text-accent',
                                        rowState === 'Functional Occupancy' && 'border-accent/60 bg-accent/10 text-accent',
                                        rowState === 'Hypermobile Override' && 'border-status-warn/70 bg-status-warn-surface text-status-warn',
                                        rowState === 'Insufficient Data' && 'border-line/60 bg-bg/10 text-muted',
                                        rowState === 'No Data' && 'border-line/60 bg-fg/5 text-muted'
                                      )}
                                    >
                                      {rowState}
                                    </Badge>
                                    {isHypermobile && <Badge className="bg-status-warn-surface text-status-warn border border-status-warn text-[9px]">Hypermobile</Badge>}
                                  </div>
                                )}
                                {isInvalidated && <Badge variant="destructive" className="mt-1 text-[9px]">{test?.invalidationReason ?? 'Invalidated'}</Badge>}
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="right" className={tooltipClass}>
                              <p className="font-semibold">{def.label}</p>
                              <p className="text-muted">Expected RLP: {getRuleDocExpectedRlp(def.key)}</p>
                              {reason && <p className="text-muted">Why now: {reason}</p>}
                              <p className="text-muted">{getRuleDocBiomechNote(def.key)}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      <TableCell className={cn(tdBase, 'text-center text-muted')}>{def.goal}</TableCell>
                      <TableCell className={cn(tdBase, 'text-center font-mono')}>
                        <TooltipProvider delayDuration={0}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="cursor-help">{leftValueText}</span>
                            </TooltipTrigger>
                            <TooltipContent className={tooltipClass}>
                              <p className="font-semibold">{def.label} - Left Value</p>
                              <p className="text-muted">Observed: {leftValueText}</p>
                              <p className="text-muted">Goal anchor: {def.goal}</p>
                              {row?.passThreshold !== null && (
                                <p className="text-muted">Pass threshold: {row.passThreshold}°</p>
                              )}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      <TableCell className={cn(tdBase, 'text-center font-mono')}>
                        <TooltipProvider delayDuration={0}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="cursor-help">{rightValueText}</span>
                            </TooltipTrigger>
                            <TooltipContent className={tooltipClass}>
                              <p className="font-semibold">{def.label} - Right Value</p>
                              <p className="text-muted">Observed: {rightValueText}</p>
                              <p className="text-muted">Goal anchor: {def.goal}</p>
                              {row?.passThreshold !== null && (
                                <p className="text-muted">Pass threshold: {row.passThreshold}°</p>
                              )}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      {isSimpleView ? (
                        <>
                          <TableCell
                            className={cn(
                              tdBase,
                              'hidden text-center text-xs sm:table-cell',
                              simpleLeftActive ? activeCellStyle(2) : 'text-muted'
                            )}
                          >
                            {simpleLeftActive ? 'L' : '—'}
                          </TableCell>
                          <TableCell
                            className={cn(
                              tdBase,
                              'hidden text-center text-xs sm:table-cell',
                              simpleRightActive ? activeCellStyle(3) : 'text-muted'
                            )}
                          >
                            {simpleRightActive ? 'R' : '—'}
                          </TableCell>
                        </>
                      ) : (
                        def.cells.map((cell, i) => (
                          <TableCell
                            key={`${def.key}-${i}`}
                            className={cn(
                              tdBase,
                              'text-center text-[11px]',
                              activeColumns.includes(i) ? activeCellStyle(i) : 'text-muted',
                            )}
                          >
                            <TooltipProvider delayDuration={0}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="cursor-help">{cell}</span>
                                </TooltipTrigger>
                                <TooltipContent className={tooltipClass}>
                                  <p className="font-semibold">{def.label} - Spectrum Cell</p>
                                  <p className="text-muted">Cell meaning: {cell}</p>
                                  <p className="text-muted">Expected RLP: {getRuleDocExpectedRlp(def.key)}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </TableCell>
                        ))
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

        </div>
  );
}
